import { Bot, Home, Lock, MessageSquare, Monitor, Rocket, Server, Sparkles } from "lucide-react";
import { store, useStore, type AppView } from "@/lib/store";
import { cn } from "@/lib/utils";

// Compact navigation strip above the session list. Platform surfaces show a
// lock affordance until an OpZero token with platform scopes is available —
// they still navigate (the views render the connect CTA), so discovery works.

interface NavItem {
  label: string;
  view: AppView;
  icon: typeof Home;
  activeNames: string[];
  platform?: boolean;
  count?: number;
}

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const state = useStore();
  const { platform } = state;

  const items: NavItem[] = [
    { label: "Home", view: { name: "home" }, icon: Home, activeNames: ["home"] },
    { label: "Sessions", view: { name: "console" }, icon: MessageSquare, activeNames: ["console"] },
    {
      label: "Projects",
      view: { name: "projects" },
      icon: Rocket,
      activeNames: ["projects", "project"],
      platform: true,
      count: platform.projects.loaded ? platform.projects.items.length : undefined,
    },
    {
      label: "Assistants",
      view: { name: "assistants" },
      icon: Bot,
      activeNames: ["assistants", "assistant"],
      platform: true,
      count: platform.assistants.loaded ? platform.assistants.items.length : undefined,
    },
    {
      label: "Servers",
      view: { name: "servers" },
      icon: Server,
      activeNames: ["servers", "server"],
      platform: true,
      count: platform.mcpServers.loaded ? platform.mcpServers.items.length : undefined,
    },
    { label: "Machines", view: { name: "machines" }, icon: Monitor, activeNames: ["machines"] },
    // The first-run fork auto-opens once per browser; this keeps the install
    // command and the Claude.ai connector URL one click away afterwards.
    { label: "Get started", view: { name: "welcome" }, icon: Sparkles, activeNames: ["welcome"] },
  ];

  return (
    <nav className="shrink-0 border-b border-border/40 px-2 py-2" aria-label="Main">
      <ul className="space-y-0.5">
        {items.map((item) => {
          const active = item.activeNames.includes(state.view.name);
          const locked = item.platform && !platform.available;
          const Icon = item.icon;
          return (
            <li key={item.label}>
              <button
                type="button"
                onClick={() => {
                  store.navigate(item.view);
                  onNavigate?.();
                }}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate text-left">{item.label}</span>
                {locked ? (
                  <Lock className="h-3 w-3 shrink-0 opacity-60" />
                ) : item.count !== undefined && item.count > 0 ? (
                  <span className="rounded-full bg-secondary px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground">
                    {item.count}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
